import React from 'react';
import { Card } from '@repo/ui/Card';
import type { Mercury } from '@repo/types/Models';
import SupportDropdown from './SupportDrowdown';

interface BadgeProps {
  text: string;
  color: 'green' | 'blue';
}

const Badge = ({ text, color }: BadgeProps) => {
  return (
    <span
      className={`${color === 'blue' ? 'text-blue-800 bg-blue-200' : ''} ${
        color === 'green' ? 'text-green-800 bg-green-200' : ''
      }  text-sm  whitespace-nowrap px-2 py-1 rounded-md w-min`}
    >
      {text}
    </span>
  );
};

interface SupportPlansViewTableProps {
  planName: Mercury.Models.AccountSupportPlan['name'];
}

const SupportPlansViewTable = ({ planName }: SupportPlansViewTableProps) => {
  const showStandardColumn = planName === 'Standard Support';
  const showProfessionalColumn = ['Standard Support', 'Professional Support'].includes(planName);

  return (
    <>
      <div className="flex flex-col gap-12">
        <Card size="no-padding">
          <table className="w-full rounded-xl border-collapse table-fixed border-hidden border-spacing-0 dark:border-grey-700">
            <thead>
              <tr className="dark:text-blue-100">
                <th className="justify-center items-center px-5 py-12 rounded-tl-xl border dark:border-grey-700 dark:bg-blue-900">
                  <h1 className="text-lg sm:text-xl sm:text-3xl">Support Features</h1>
                </th>
                {showStandardColumn && (
                  <th className="px-5 h-full border dark:border-grey-700 dark:bg-blue-900">
                    <div className="flex flex-col gap-2 items-center text-lg font-normal sm:text-2xl">
                      <span>Standard</span>
                      {planName === 'Standard Support' && (
                        <Badge text="Current Plan" color="blue" />
                      )}
                    </div>
                  </th>
                )}
                {showProfessionalColumn && (
                  <th className="px-5 h-full border dark:border-grey-700 dark:bg-blue-900">
                    <div className="flex flex-col gap-2 items-center text-lg font-normal sm:text-2xl">
                      <span>Professional</span>
                      {planName === 'Professional Support' && (
                        <Badge text="Current Plan" color="blue" />
                      )}
                    </div>
                  </th>
                )}
                <th
                  className={`justify-center items-center px-5 h-full rounded-tr-xl border dark:border-grey-700 dark:bg-blue-900`}
                >
                  <div className="flex flex-col gap-2 items-center text-lg font-normal sm:text-2xl">
                    <span className="">Enterprise</span>
                    {planName === 'Enterprise Support' && (
                      <Badge text="Current Plan" color="blue" />
                    )}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="[&>tr>td]:p-5">
              <tr className="sm:text-center dark:text-blue-100 dark:bg-blue-1000">
                <td className="h-full border dark:border-grey-700">
                  <span className="text-lg font-bold text-left sm:text-xl">Self serve</span>
                </td>
                {showStandardColumn && (
                  <td className="h-full border dark:border-grey-700">
                    <span className="text-4xl material-symbols-outlined dark:text-blue-100">
                      check
                    </span>
                  </td>
                )}
                {showProfessionalColumn && (
                  <td className="h-full border dark:border-grey-700">
                    <span className="text-4xl material-symbols-outlined dark:text-blue-100">
                      check
                    </span>
                  </td>
                )}
                <td className="h-full border dark:border-grey-700">
                  <span className="text-4xl material-symbols-outlined dark:text-blue-100">
                    check
                  </span>
                </td>
              </tr>
              <tr className="sm:text-center dark:text-blue-100 dark:bg-blue-1000">
                <td className="h-full border dark:border-grey-700">
                  <span className="text-lg font-bold text-left sm:text-xl">Email support</span>
                </td>
                {showStandardColumn && (
                  <td className="h-full border dark:border-grey-700">
                    <span className="text-4xl material-symbols-outlined dark:text-blue-200">
                      check
                    </span>
                  </td>
                )}
                {showProfessionalColumn && (
                  <td className="h-full border dark:border-grey-700">
                    <span className="text-4xl material-symbols-outlined dark:text-blue-200">
                      check
                    </span>
                  </td>
                )}
                <td className="h-full border dark:border-grey-700">
                  <span className="text-4xl material-symbols-outlined dark:text-blue-200">
                    check
                  </span>
                </td>
              </tr>
              <tr className="sm:text-center dark:text-blue-200 dark:bg-blue-1000">
                <td className="h-full border dark:border-grey-700">
                  <span className="text-lg font-bold text-left sm:text-xl">MTTR</span>
                </td>
                {showStandardColumn && (
                  <td className="h-full border dark:border-grey-700">
                    <span className="text-lg sm:text-xl">Best Effort</span>
                  </td>
                )}
                {showProfessionalColumn && (
                  <td className="h-full border dark:border-grey-700">
                    <span className="text-4xl material-symbols-outlined">check</span>
                  </td>
                )}
                <td className="h-full border dark:border-grey-700">
                  <span className="text-4xl material-symbols-outlined">check</span>
                </td>
              </tr>
              <tr className="sm:text-center dark:text-blue-200 dark:bg-blue-1000">
                <td className="h-full border dark:border-grey-700">
                  <span className="text-lg font-bold text-left sm:text-xl">SLA</span>
                </td>
                {showStandardColumn && (
                  <td className="h-full border dark:border-grey-700">
                    <span className="text-lg sm:text-xl">Best Effort</span>
                  </td>
                )}
                {showProfessionalColumn && (
                  <td className="h-full border dark:border-grey-700">
                    <span className="text-lg sm:text-xl">99.99%</span>
                  </td>
                )}
                <td className="h-full border dark:border-grey-700">
                  <span className="text-lg sm:text-xl">99.99%</span>
                </td>
              </tr>
              <tr className="sm:text-center dark:text-blue-100 dark:bg-blue-1000">
                <td className="h-full border dark:border-grey-700">
                  <span className="text-lg font-bold text-left sm:text-xl">Phone support</span>
                </td>
                {showStandardColumn && (
                  <td className="h-full border dark:border-grey-700">
                    <div className="mx-auto w-4 h-[1px] dark:bg-blue-200"></div>
                  </td>
                )}
                {showProfessionalColumn && (
                  <td className="h-full border dark:border-grey-700">
                    <span className="text-4xl material-symbols-outlined dark:text-blue-200">
                      check
                    </span>
                  </td>
                )}
                <td className="h-full border dark:border-grey-700">
                  <span className="text-4xl material-symbols-outlined dark:text-blue-200">
                    check
                  </span>
                </td>
              </tr>
              <tr className="sm:text-center dark:text-blue-100 dark:bg-blue-1000">
                <td className="h-full rounded-bl-xl border dark:border-grey-700">
                  <span className="text-lg font-bold text-left sm:text-xl">
                    24x7 emergency support
                  </span>
                </td>
                {showStandardColumn && (
                  <td className="h-full border dark:border-grey-700">
                    <div className="mx-auto w-4 h-[1px] dark:bg-blue-200"></div>
                  </td>
                )}
                {showProfessionalColumn && (
                  <td className="h-full border dark:border-grey-700">
                    <div className="mx-auto w-4 h-[1px] dark:bg-blue-200"></div>
                  </td>
                )}
                <td className="h-full rounded-br-xl border dark:border-grey-700">
                  <span className="text-4xl material-symbols-outlined dark:text-blue-200">
                    check
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </Card>
        <Card size="no-padding">
          <table className="w-full rounded-xl border-collapse table-fixed border-hidden border-spacing-0 dark:border-grey-700">
            <thead>
              <tr className="dark:text-blue-100">
                <th className="justify-center items-center px-5 py-12 rounded-tl-xl border dark:border-grey-700 dark:bg-blue-900">
                  <h1 className="text-lg sm:text-xl sm:text-3xl">
                    Guaranteed Response Times (MTTR)
                  </h1>
                </th>
                {showStandardColumn && (
                  <th className="px-5 h-full border dark:border-grey-700 dark:bg-blue-900">
                    <div className="flex flex-col gap-2 items-center text-lg font-normal sm:text-2xl">
                      <span>Standard</span>
                      {planName === 'Standard Support' && (
                        <Badge text="Current Plan" color="blue" />
                      )}
                    </div>
                  </th>
                )}
                {showProfessionalColumn && (
                  <th className="px-5 h-full border dark:border-grey-700 dark:bg-blue-900">
                    <div className="flex flex-col gap-2 items-center text-lg font-normal sm:text-2xl">
                      <span>Professional</span>
                      {planName === 'Professional Support' && (
                        <Badge text="Current Plan" color="blue" />
                      )}
                    </div>
                  </th>
                )}
                <th
                  className={`justify-center items-center px-5 h-full rounded-tr-xl border dark:border-grey-700 dark:bg-blue-900`}
                >
                  <div className="flex flex-col gap-2 items-center text-lg font-normal sm:text-2xl">
                    <span className="">Enterprise</span>
                    {planName === 'Enterprise Support' && (
                      <Badge text="Current Plan" color="blue" />
                    )}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="[&>tr>td]:p-5">
              <tr className="sm:text-center dark:text-blue-100 dark:bg-blue-1000">
                <td className="h-full border dark:border-grey-700">
                  <SupportDropdown
                    title="Priority 1 Ticket"
                    description="Critical Live Application Failure. This represents a total loss of service and/or a service degrade that renders the API SMS feature as unavailable with no remedy. This is not for SDK services, or clients still in development."
                  />
                </td>
                {showStandardColumn && (
                  <td className="h-full border dark:border-grey-700">
                    <span className="text-lg sm:text-xl dark:text-blue-100">
                      Within 8 Business Hours
                    </span>
                  </td>
                )}
                {showProfessionalColumn && (
                  <td className="h-full border dark:border-grey-700">
                    <span className="text-lg sm:text-xl dark:text-blue-100">
                      Within 2 Business Hours
                    </span>
                  </td>
                )}
                <td className="h-full border dark:border-grey-700">
                  <span className="text-lg sm:text-xl dark:text-blue-100">
                    Within 1 hour, 24 / 7
                  </span>
                </td>
              </tr>
              <tr className="sm:text-center dark:text-blue-100 dark:bg-blue-1000">
                <td className="h-full border dark:border-grey-700">
                  <SupportDropdown
                    title="Priority 2 Ticket"
                    description="Service Degrade. This represents intermittent service disruption such as SMS Gateway unavailability, and degradation of service response on messaging. This is not for SDK services or clients still in development."
                  />
                </td>
                {showStandardColumn && (
                  <td className="h-full border dark:border-grey-700">
                    <span className="text-lg sm:text-xl dark:text-blue-200">Best Effort</span>
                  </td>
                )}
                {showProfessionalColumn && (
                  <td className="h-full border dark:border-grey-700">
                    <span className="text-lg sm:text-xl dark:text-blue-200">
                      Within 3 Business Hours
                    </span>
                  </td>
                )}
                <td className="h-full border dark:border-grey-700">
                  <span className="text-lg sm:text-xl dark:text-blue-200">
                    Within 2 Business Hours
                  </span>
                </td>
              </tr>
              <tr className="sm:text-center dark:text-blue-200 dark:bg-blue-1000">
                <td className="h-full border dark:border-grey-700">
                  <SupportDropdown
                    title="Priority 3 Ticket"
                    description="General Disruption. This represents service inquiries, upgrade requests, and development questions."
                  />
                </td>
                {showStandardColumn && (
                  <td className="h-full border dark:border-grey-700">
                    <span className="text-lg sm:text-xl">Best Effort</span>
                  </td>
                )}
                {showProfessionalColumn && (
                  <td className="h-full border dark:border-grey-700">
                    <span className="text-lg sm:text-xl">Within 6 Business Hours</span>
                  </td>
                )}
                <td className="h-full border dark:border-grey-700">
                  <span className="text-lg sm:text-xl">Within 3 Business Hours</span>
                </td>
              </tr>
            </tbody>
          </table>
        </Card>
      </div>
    </>
  );
};

export default SupportPlansViewTable;
